import { call, put } from "redux-saga/effects";
import axios from "../../../services/axios";

// Growing-batch sizes: 1000, 2000, 4000, ..., capped at MAX_BATCH_SIZE so no
// single request ever has to fetch/process an unbounded amount of data,
// regardless of how large the underlying dataset gets.
export const INITIAL_BATCH_SIZE = 1000;
export const MAX_BATCH_SIZE = 5000;

export function* fetchPage(url, skip, limit) {
  return yield call(axios.request, {
    method: "GET",
    url,
    params: { skip, limit },
    headers: {
      "Content-Type": "application/json",
      Authorization: localStorage.getItem("token1"),
    },
  });
}

// Merges a freshly-fetched batch into the accumulated list, collapsing any
// record whose identity was already seen. Batches can overlap (e.g. multiple
// records sharing the same sort key at a page boundary), so de-duping here
// is what keeps the accumulated array from growing past the real total.
// A plain Set doesn't work for this: Set dedupes by reference, and every
// batch parses fresh objects from JSON, so two records with the same `uid`
// would still be different references. A Map keyed by identity gives the
// same "no duplicates" guarantee but keyed on a value, not a reference.
// Prefers `uid` (the app-level id), falling back to `_id` (Mongo's own
// guaranteed-unique, always-present id) only when `uid` is missing — some
// derived/joined collections (e.g. view_jobroleinvite_statistics) don't
// enforce `uid` being set on every record, so multiple distinct records can
// share `uid: undefined`; without the fallback they'd wrongly collapse into
// a single record and real data would get dropped.
export function mergeUniqueByUid(items, newRecords) {
  const byKey = new Map(items.map((record) => [record.uid ?? record._id, record]));
  for (const record of newRecords) {
    byKey.set(record.uid ?? record._id, record);
  }
  return [...byKey.values()];
}

// Fetches `url` in growing batches, calling `onBatch(itemsSoFar, total, done)`
// after every batch and dispatching whatever actions it returns. This lets a
// list render/update progressively instead of waiting for the full dataset.
// `done` is true only on the last batch — callers use it to know when it's
// safe to update (and cache) a "Total" display value, instead of doing so on
// every batch while the count is still climbing.
//
// `total` is deliberately the accumulated array length, not the backend's
// separately-fetched countDocuments() value. Those are two independently
// computed numbers that can drift apart (different query, different moment
// in time); deriving total from the same data that populates the list
// guarantees a "Total" badge can never disagree with the actual rows shown.
export function* fetchListInBatches(url, onBatch) {
  let items = [];
  let skip = 0;
  let limit = INITIAL_BATCH_SIZE;
  let done = false;

  while (!done) {
    const res = yield call(fetchPage, url, skip, limit);
    items = mergeUniqueByUid(items, res.data);
    skip += res.data.length;
    done = res.data.length < limit;
    limit = Math.min(limit * 2, MAX_BATCH_SIZE);

    const actions = onBatch(items, items.length, done) || [];
    for (const action of actions) {
      yield put(action);
    }
  }
}
